/** DOM HUD — French UI text lives here, not in the game logic. */

import type { SurfaceId } from '@microligue/shared';
import { SURFACE_LABELS } from '@microligue/shared';
import { SURFACE_COLORS } from './surfaces';

export function formatLapTime(ms: number): string {
  const clamped = Math.max(0, Math.round(ms));
  const minutes = Math.floor(clamped / 60000);
  const seconds = Math.floor((clamped % 60000) / 1000);
  const millis = clamped % 1000;
  return `${minutes}:${String(seconds).padStart(2, '0')}.${String(millis).padStart(3, '0')}`;
}

/** Gap in seconds, e.g. "1,234 s". */
function formatGap(ms: number): string {
  return `${(ms / 1000).toFixed(3).replace('.', ',')} s`;
}

export class Hud {
  private readonly speedEl: HTMLElement;
  private readonly lapCurrentEl: HTMLElement;
  private readonly lapLastEl: HTMLElement;
  private readonly lapBestEl: HTMLElement;
  private readonly recordEl: HTMLElement;
  private readonly toastsEl: HTMLElement;
  private readonly fpsEl: HTMLElement;
  private readonly wrongWayEl: HTMLElement;
  private readonly surfaceEl: HTMLElement;
  private readonly surfaceSwatchEl: HTMLElement;
  private readonly pingEl: HTMLElement;
  private readonly syncEl: HTMLElement;
  private readonly playersEl: HTMLElement;
  private readonly raceEl: HTMLElement;
  private readonly positionEl: HTMLElement;
  private readonly lapsEl: HTMLElement;
  private readonly gapsEl: HTMLElement;
  private lastSurface: SurfaceId | null = null;
  /**
   * Dernier texte écrit par élément. Réécrire un textContent IDENTIQUE
   * invalide quand même style/layout ; appelé 60×/s depuis la boucle de jeu
   * (vitesse, ping, sync, bloc course…), ce sont des recalculs par frame
   * offerts au navigateur — coûteux précisément sur les machines modestes.
   */
  private readonly lastText = new Map<HTMLElement, string>();

  /** textContent, seulement si le texte a réellement changé. */
  private setText(el: HTMLElement, text: string): void {
    if (this.lastText.get(el) === text) return;
    this.lastText.set(el, text);
    el.textContent = text;
  }

  constructor(doc: Document) {
    this.speedEl = mustGet(doc, 'hud-speed');
    this.lapCurrentEl = mustGet(doc, 'hud-lap-current');
    this.lapLastEl = mustGet(doc, 'hud-lap-last');
    this.lapBestEl = mustGet(doc, 'hud-lap-best');
    this.recordEl = mustGet(doc, 'hud-record');
    this.toastsEl = mustGet(doc, 'hud-toasts');
    this.fpsEl = mustGet(doc, 'hud-fps');
    this.wrongWayEl = mustGet(doc, 'hud-wrongway');
    this.surfaceEl = mustGet(doc, 'hud-surface-label');
    this.surfaceSwatchEl = mustGet(doc, 'hud-surface-swatch');
    this.pingEl = mustGet(doc, 'hud-ping');
    this.syncEl = mustGet(doc, 'hud-sync');
    this.playersEl = mustGet(doc, 'hud-players');
    this.raceEl = mustGet(doc, 'hud-race');
    this.positionEl = mustGet(doc, 'hud-position');
    this.lapsEl = mustGet(doc, 'hud-laps');
    this.gapsEl = mustGet(doc, 'hud-gaps');
  }

  /** Race block (position, lap, gaps) — hidden outside of a race. */
  setRaceInfo(
    info: {
      pos: number;
      total: number;
      lap: number;
      laps: number;
      gapAheadMs: number | null;
      gapBehindMs: number | null;
      finished: boolean;
    } | null,
  ): void {
    if (!info) {
      if (!this.raceEl.hidden) this.raceEl.hidden = true;
      return;
    }
    if (this.raceEl.hidden) this.raceEl.hidden = false;
    this.setText(
      this.positionEl,
      info.finished
        ? `🏁 ${info.pos === 1 ? '1ʳᵉ' : `${info.pos}ᵉ`} / ${info.total}`
        : `P${info.pos} / ${info.total}`,
    );
    this.setText(this.lapsEl, `Tour ${info.lap} / ${info.laps}`);
    const parts: string[] = [];
    if (info.gapAheadMs !== null) parts.push(`▲ +${formatGap(info.gapAheadMs)}`);
    if (info.gapBehindMs !== null) parts.push(`▼ −${formatGap(info.gapBehindMs)}`);
    this.setText(this.gapsEl, parts.join('   '));
  }

  /** Speed in px/s, displayed as stylized km/h (×0.35 — 420 px/s ≈ 147 km/h). */
  setSpeed(pxPerSec: number): void {
    this.setText(this.speedEl, `${Math.round(pxPerSec * 0.35)} km/h`);
  }

  setSurface(surface: SurfaceId): void {
    if (surface === this.lastSurface) return;
    this.lastSurface = surface;
    this.surfaceEl.textContent = SURFACE_LABELS[surface];
    this.surfaceSwatchEl.style.backgroundColor = SURFACE_COLORS[surface];
  }

  setPing(ms: number): void {
    this.setText(this.pingEl, ms > 0 ? `${Math.round(ms)} ms` : '— ms');
  }

  /**
   * Clock-sync telemetry: unacked input backlog + pace lean. A backlog that
   * climbs over a long race is the drift signature the pace servo cancels —
   * this line turns a « ça se dégrade » report into a diagnosis.
   */
  setNetSync(pendingInputs: number, pace: number): void {
    const leanPct = (pace - 1) * 100;
    const lean = leanPct === 0 ? '' : ` · ${leanPct > 0 ? '+' : ''}${leanPct.toFixed(2)}%`;
    this.setText(this.syncEl, `file ${pendingInputs}${lean}`);
  }

  setPlayers(
    players: { pseudo: string; color: string; connected: boolean; isSelf: boolean }[],
  ): void {
    this.playersEl.replaceChildren(
      ...players.map((p) => {
        const item = document.createElement('span');
        item.className = 'hud-player' + (p.connected ? '' : ' hud-player-off');
        const dot = document.createElement('span');
        dot.className = 'hud-player-dot';
        dot.style.backgroundColor = p.color;
        const name = document.createElement('span');
        name.textContent = p.isSelf ? `${p.pseudo} (vous)` : p.pseudo;
        item.append(dot, name);
        return item;
      }),
    );
  }

  setLapLive(currentMs: number | null): void {
    this.setText(
      this.lapCurrentEl,
      `Tour : ${currentMs === null ? '—' : formatLapTime(currentMs)}`,
    );
  }

  setLastLap(timeMs: number, valid: boolean): void {
    this.setText(this.lapLastEl, `Dernier : ${formatLapTime(timeMs)}${valid ? '' : ' (invalide)'}`);
  }

  /**
   * Best lap line. During a race this is the best lap OF THE RACE (SPEC §10);
   * otherwise the all-time personal best on this circuit.
   */
  setPersonalBest(ms: number | null, scope: 'perso' | 'course' = 'perso'): void {
    const label = scope === 'course' ? 'Meilleur (course)' : 'Meilleur (vous)';
    this.setText(this.lapBestEl, `${label} : ${ms === null ? '—' : formatLapTime(ms)}`);
  }

  setRecord(ms: number | null, holder: string | null): void {
    this.setText(
      this.recordEl,
      ms === null ? 'Record : —' : `Record : ${formatLapTime(ms)} (${holder ?? '?'})`,
    );
  }

  resetLapDisplays(): void {
    this.setLapLive(null);
    this.setText(this.lapLastEl, 'Dernier : —');
    this.setPersonalBest(null);
    this.setRecord(null, null);
  }

  toast(text: string, kind: 'info' | 'record' | 'invalid' = 'info'): void {
    const el = document.createElement('div');
    el.className =
      'hud-toast' +
      (kind === 'record' ? ' toast-record' : kind === 'invalid' ? ' toast-invalid' : '');
    el.textContent = text;
    this.toastsEl.appendChild(el);
    window.setTimeout(() => el.remove(), 4500);
  }

  setWrongWay(active: boolean): void {
    if (this.wrongWayEl.hidden === active) this.wrongWayEl.hidden = !active;
  }

  /**
   * FPS + télémétrie de fluidité de la fenêtre écoulée : plus long trou entre
   * deux frames (la « saccade » vécue), et tas JS si le navigateur l'expose.
   * Lecture directe pour un rapport de playtest : tas qui grimpe = mémoire qui
   * s'accumule ; tas stable mais gels qui s'allongent = CPU/GPU (thermique…).
   */
  setFps(fps: number, maxGapMs?: number, heapMb?: number | null, note?: string): void {
    let text = `${Math.round(fps)} FPS`;
    if (maxGapMs !== undefined && maxGapMs > 0) text += ` · gel max ${Math.round(maxGapMs)} ms`;
    if (heapMb !== undefined && heapMb !== null) text += ` · tas ${heapMb.toFixed(0)} Mo`;
    if (note) text += ` · ${note}`;
    this.setText(this.fpsEl, text);
  }
}

function mustGet(doc: Document, id: string): HTMLElement {
  const el = doc.getElementById(id);
  if (!el) throw new Error(`Élément HUD introuvable : #${id}`);
  return el;
}
